import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Svg } from '@/shared/ui/Svg';
import { formatPercentOrDash } from '@/shared/lib/utils/formatPercent';
import ChevronDownIcon from '@/shared/assets/icons/chevron-down.svg';

type StudentRow = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  progressByModules: number | null;
  progressByLessons: number | null;
  attendance: number | null;
  overallActivity?: number;
  lastActivity: string | null;
  registrationDate: string | null;
};

const formatDateTime = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return {
      date: format(date, 'dd.MM.yyyy'),
      time: format(date, 'HH:mm'),
    };
  } catch {
    return { date: dateString, time: '' };
  }
};

const formatActivity = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return `${hours}h ${mins} min`;
  }
  return `${mins} min`;
};

export const studentsColumns = (): ColumnDef<StudentRow>[] => [
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>ID</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'courseIdentifier',
    cell: ({ row }) => <span className="text-black-headline">{row.original.id}</span>,
  },
  {
    header: 'User',
    accessorFn: ({ firstName, lastName }) => `${firstName} ${lastName}`,
    cell: ({ row }) => (
      <div className="flex flex-col">
        <span className="text-black-headline font-medium">
          {row.original.firstName} {row.original.lastName}
        </span>
        <span className="text-grey-secondary text-sm">{row.original.email}</span>
      </div>
    ),
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>Progress</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'progressByLessons',
    cell: ({ row }) => (
      <div className="flex flex-col gap-0.5 whitespace-nowrap">
        <span className="text-black-headline">
          {formatPercentOrDash(row.original.progressByLessons)} lessons
        </span>
        <span className="text-grey-secondary text-xs">
          {formatPercentOrDash(row.original.progressByModules)} modules
        </span>
      </div>
    ),
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>Attendance</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'attendance',
    cell: ({ row }) => (
      <span className="text-black-headline">{formatPercentOrDash(row.original.attendance)}</span>
    ),
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>Overall activity</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'overallActivity',
    cell: ({ row }) => {
      const activity = row.original.overallActivity;
      return typeof activity === 'number' ? (
        <span className="text-black-headline">{formatActivity(activity)}</span>
      ) : (
        <span className="text-grey-secondary">—</span>
      );
    },
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>Last Activity</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'lastActivity',
    cell: ({ row }) => {
      const dateStr = row.original.lastActivity;
      if (!dateStr) return <span className="text-grey-secondary">—</span>;
      const lastActivity = formatDateTime(dateStr);
      return (
        <div className="flex flex-col">
          <span className="text-black-headline">{lastActivity.date}</span>
          <span className="text-grey-secondary text-sm">{lastActivity.time}</span>
        </div>
      );
    },
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>Registration Date</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'registrationDate',
    cell: ({ row }) => {
      const dateStr = row.original.registrationDate;
      if (!dateStr) return <span className="text-grey-secondary">—</span>;
      try {
        const date = new Date(dateStr);
        return <span className="text-black-headline">{format(date, 'dd.MM.yyyy')}</span>;
      } catch {
        return <span className="text-grey-secondary">—</span>;
      }
    },
  },
];
